import { observer } from "mobx-react";
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
    Container,
    ContainerChatMessageInput,
    ContainerScreenChat,
    ContentChatMessageAndInput, ContentLoading, Icon, LoadingMoreMessageBottom, LoadingMoreMessageTop,
} from "@dropDesk/presentation/components/chat_view/chat_message/styled";
import PickFileGeneric from "@dropDesk/presentation/components/drop_zone/pick_file_generic";
import InputMessage from "@dropDesk/presentation/components/chat_view/chat_message/input_message";
import { TicketMessageEntity } from "@dropDesk/domain/entities/ticket/message/ticket_message.entity";
import { UserEntity } from "@dropDesk/domain/entities/user/user.entity";
import { useInjection } from "inversify-react";
import { AppController } from "@dropDesk/presentation/app/app.controller";
import MessagesBox from "@dropDesk/presentation/components/chat_view/chat_message/message_box";
import { AudioRecordEntity } from "@dropDesk/domain/entities/common/audio_record.entity";
import firebaseStorage from "firebase/storage";
import UploadTask = firebaseStorage.UploadTask;
import { ReplyEntity } from "@dropDesk/domain/entities/ticket/message/external/message_entity";
import { Brightness } from "@dropDesk/domain/entities/theme/colors.entity";
import ClipLoading from "@dropDesk/presentation/components/loadings/loading_clip";
import ButtonNavigateToBottom from "@dropDesk/presentation/components/buttons/button_navigate_to_bottom";
import { ChatMessageEntity } from "@dropDesk/domain/entities/messages/message_enums";
import LoadAdjacentTicketButton from "@dropDesk/presentation/components/chat_view/chat_message/load_previous_ticket_button";

const findNextTicketBoundaryMessage = (
    list: ChatMessageEntity[],
    ticketId: string
): ChatMessageEntity | undefined => {
    const ticketMessages = list.filter(
        (message) => message instanceof TicketMessageEntity && message.ticket?.id === ticketId
    );
    if (ticketMessages.length === 0) {
        return undefined;
    }
    const byPosition = ticketMessages.find(
        (message) => message.isFirstPosition || message.isUniquePosition
    );
    return byPosition ?? ticketMessages[ticketMessages.length - 1];
};

export type ChildRefType = {
    handleAllChangesImperative: (message: ReplyEntity | null) => void;
    getCurrentMessage: () => TicketMessageEntity;
    setInputFocus: () => void;
};


const ChatMessage = observer(
    ({
        handlePickFile,
        handleOnScrollTop,
        handleOnScrollBottom,
        compactModeMessages,
        userLogged,
        canChange,
        canSendMessage,
        forcePrivateComment,
        canEditFile,
        userClient,
        attendant,
        messages,
        idMessageFocused,
        handleClickOnMessageReply,
        cancelUpload,
        onRetrySendMessageError,
        topMessageLoading,
        bottomMessageLoading,
        sendMessage,
        handleRemoveReaction,
        handleAddReaction,
        lastUsedReactions,
        sendAudio,
        handleDeleteMessage,
        handleEditMessage,
        handleResetMessageFocused,
        idParent,
        handleMoveCursorToFinal,
        targetMessagePaginationTop,
        targetMessagePaginationBottom,
        targetMessageButtonNavigateBottom,
        allReactions,
        stopLoading,
        isMeAttendingTicket,
        ticketIsOpen,
        childRef,
        targetVirtualizedMessage,
        handleScrollTopVirtualizedMessage,
        showOriginalFile,
        setLoaded,
        createNewMessage,
        startingServiceInternalChatHeaderText,
        closingServiceInternalChatHeaderText,
        useStrictPaginationTopObserver,
        showLoadPreviousTicketButton,
        onLoadPreviousTicket,
        loadingPreviousTicket,
        showLoadNextTicketButton,
        onLoadNextTicket,
        loadingNextTicket,
        forceShowNavigateToBottom,
        nextTicketId,
        hasNextTicketAtBoundary,
    }: {
        handlePickFile: (files: File[]) => void
        handleOnScrollTop: () => Promise<boolean>
        handleOnScrollBottom: () => Promise<boolean>
        compactModeMessages: boolean,
        userLogged: UserEntity
        canChange: boolean,
        canSendMessage: boolean,
        forcePrivateComment: boolean,
        canEditFile: boolean,
        userClient: UserEntity | null,
        attendant?: UserEntity,
        messages: ChatMessageEntity[],
        idMessageFocused: string | null
        handleClickOnMessageReply: (reply: ReplyEntity) => void
        cancelUpload: (uploadTask?: UploadTask) => void
        onRetrySendMessageError: (message: ChatMessageEntity) => void
        topMessageLoading: boolean
        bottomMessageLoading: boolean,
        sendMessage: (message: ChatMessageEntity) => Promise<void>
        handleRemoveReaction: (reaction: ChatMessageEntity, messageReacted: ChatMessageEntity) => Promise<void>
        handleAddReaction: (reaction: string, messageToReacted: ChatMessageEntity) => Promise<void>
        lastUsedReactions: string[]
        sendAudio: (message: ChatMessageEntity, audioRecorder: AudioRecordEntity) => void
        handleDeleteMessage: (message: ChatMessageEntity) => void
        handleEditMessage: (editText: string, currentMessage: ChatMessageEntity) => void
        handleResetMessageFocused: () => void
        handleMoveCursorToFinal: () => void
        idParent: string
        targetMessagePaginationTop?: ChatMessageEntity
        targetMessagePaginationBottom?: ChatMessageEntity
        targetMessageButtonNavigateBottom?: ChatMessageEntity
        targetVirtualizedMessage: ChatMessageEntity | null
        allReactions: ChatMessageEntity[],
        stopLoading: () => void
        isMeAttendingTicket: boolean
        ticketIsOpen: boolean
        childRef: React.MutableRefObject<ChildRefType | null>
        handleScrollTopVirtualizedMessage: () => void
        showOriginalFile: (message: ChatMessageEntity) => void
        setLoaded: (message: ChatMessageEntity) => void
        createNewMessage: (forcePrivateComment?: boolean) => ChatMessageEntity
        startingServiceInternalChatHeaderText?: string
        closingServiceInternalChatHeaderText?: string
        useStrictPaginationTopObserver?: boolean
        showLoadPreviousTicketButton?: boolean
        onLoadPreviousTicket?: () => void
        loadingPreviousTicket?: boolean
        showLoadNextTicketButton?: boolean
        onLoadNextTicket?: () => void | Promise<void | boolean>
        loadingNextTicket?: boolean
        forceShowNavigateToBottom?: boolean
        nextTicketId?: string | null
        hasNextTicketAtBoundary?: boolean
    }
    ) => {
        const appController = useInjection(AppController);
        const colors = appController.theme.colorScheme;
        const containerChatRef = useRef<HTMLDivElement>(null);
        const scrollAnchorNextTicketIdRef = useRef<string | null>(null);
        const scrollAnchorFallbackIdRef = useRef<string | null>(null);
        const pendingOlderScrollRestoreRef = useRef(false);
        const scrollAnchorOlderIdRef = useRef<string | null>(null);
        const scrollMetricsOlderRef = useRef<{ scrollTop: number; scrollHeight: number } | null>(null);
        const isDarkTheme = colors.brightness === Brightness.dark;
        const [showButtonMoveScrollToBottom, setShowButtonMoveScrollToBottom] = useState(false);

        const handleOnScrollTopWithPreserve = async () => {
            if (pendingOlderScrollRestoreRef.current) {
                return;
            }
            const container = containerChatRef.current;
            if (container) {
                scrollMetricsOlderRef.current = {
                    scrollTop: container.scrollTop,
                    scrollHeight: container.scrollHeight,
                };
            }
            scrollAnchorOlderIdRef.current =
                targetMessagePaginationTop?.id ?? messages[messages.length - 1]?.id ?? null;
            pendingOlderScrollRestoreRef.current = true;
            await handleOnScrollTop();
        };

        useEffect(() => {
            if (targetMessagePaginationTop) {
                const messageTriggerTop = document.getElementById(targetMessagePaginationTop.id);
                if (useStrictPaginationTopObserver && !containerChatRef.current) {
                    return;
                }
                const intersectionObserver = new IntersectionObserver(entries => {
                    const shouldLoad = useStrictPaginationTopObserver
                        ? entries.some(entry => entry.isIntersecting)
                        : entries.some(entry => (entry.isIntersecting || entry.boundingClientRect.bottom > 0));
                    if (shouldLoad) {
                        handleOnScrollTopWithPreserve().then(() => stopLoading());
                    }
                }, useStrictPaginationTopObserver
                    ? { root: containerChatRef.current, threshold: 0 }
                    : undefined);
                if (messageTriggerTop) {
                    intersectionObserver.observe(messageTriggerTop);
                }
                return () => {
                    return intersectionObserver.disconnect();
                }

            }
        }, [targetMessagePaginationTop, useStrictPaginationTopObserver, messages]);

        useEffect(() => {
            if (targetVirtualizedMessage) {

                const messageTriggerVirtualizedMessage = document.getElementById(targetVirtualizedMessage.id);
                const intersectionObserver = new IntersectionObserver(entries => {
                    if (entries.some(entry => (entry.isIntersecting || entry.boundingClientRect.bottom > 0))) {
                        handleScrollTopVirtualizedMessage();
                    }

                })
                if (messageTriggerVirtualizedMessage) intersectionObserver.observe(messageTriggerVirtualizedMessage);
                return () => {
                    return intersectionObserver.disconnect();
                }

            }
        }, [targetVirtualizedMessage]);

        useEffect(() => {
            if (targetMessagePaginationBottom) {

                const messageTriggerBottom = document.getElementById(targetMessagePaginationBottom.id);
                const intersectionObserver = new IntersectionObserver(entries => {
                    if (entries.some(entry => (entry.isIntersecting || entry.boundingClientRect.top < 0))) {
                        loadMoreMessagesBottom();
                    }

                })
                if (messageTriggerBottom) intersectionObserver.observe(messageTriggerBottom);
                return () => {
                    return intersectionObserver.disconnect();
                }

            }
        }, [targetMessagePaginationBottom]);

        useEffect(() => {
            if (!targetMessageButtonNavigateBottom) {
                setShowButtonMoveScrollToBottom(false);
                return;
            }

            const container = containerChatRef.current;
            const messageEl = document.getElementById(targetMessageButtonNavigateBottom.id);
            if (!container || !messageEl) {
                setShowButtonMoveScrollToBottom(!!forceShowNavigateToBottom);
                return;
            }

            const hasScroll = container.scrollHeight > container.clientHeight;
            if (forceShowNavigateToBottom && !hasScroll) {
                setShowButtonMoveScrollToBottom(true);
                return;
            }

            const intersectionObserver = new IntersectionObserver(entries => {
                setShowButtonMoveScrollToBottom(
                    entries.some(entry => !entry.isIntersecting)
                );
            }, {root: container, threshold: 0});

            intersectionObserver.observe(messageEl);
            return () => {
                intersectionObserver.disconnect();
            };
        }, [targetMessageButtonNavigateBottom, forceShowNavigateToBottom, messages]);

        const moveScrollToBottom = () => {
            if (containerChatRef.current) {
                containerChatRef.current.scrollTop = 0;
            }
        };

        const loadMoreMessagesBottom = async () => {
            const lastMessage = messages[0];
            const lastElement = document.getElementById(lastMessage.id);
            scrollAnchorFallbackIdRef.current = lastMessage?.id ?? null;
            if (nextTicketId && hasNextTicketAtBoundary) {
                scrollAnchorNextTicketIdRef.current = nextTicketId;
            }
            const loadedMoreMessage = await handleOnScrollBottom();
            if (
                loadedMoreMessage
                && !scrollAnchorNextTicketIdRef.current
                && lastElement
                && containerChatRef.current
                && containerChatRef.current.scrollTop === 0
            ) {
                lastElement.scrollIntoView({
                    block: 'end',
                    behavior: 'auto',
                });
            }
            stopLoading();
        }

        const setMessageToReply = (message: ReplyEntity | null): void => {
            if (childRef.current) {
                childRef.current.handleAllChangesImperative(message);
            }
        };

        const handleOnClickMoveCursorToFinal = () => {
            setShowButtonMoveScrollToBottom(false);
            moveScrollToBottom();
            handleMoveCursorToFinal();
        }

        const handleLoadNextTicket = async () => {
            scrollAnchorFallbackIdRef.current = messages[0]?.id ?? null;
            scrollAnchorNextTicketIdRef.current = nextTicketId ?? null;
            await onLoadNextTicket?.();
        }

        useLayoutEffect(() => {
            if (!pendingOlderScrollRestoreRef.current) {
                return;
            }
            const container = containerChatRef.current;
            const metrics = scrollMetricsOlderRef.current;

            if (container && metrics) {
                const heightDiff = container.scrollHeight - metrics.scrollHeight;
                container.scrollTop = metrics.scrollTop + heightDiff;
            }

            const anchorId = scrollAnchorOlderIdRef.current;
            if (anchorId) {
                const anchorEl = document.getElementById(anchorId);
                if (anchorEl) {
                    anchorEl.scrollIntoView({
                        block: 'nearest',
                        behavior: 'auto',
                    });
                }
            }

            pendingOlderScrollRestoreRef.current = false;
            scrollAnchorOlderIdRef.current = null;
            scrollMetricsOlderRef.current = null;
        }, [messages]);

        useLayoutEffect(() => {
            if (!loadingNextTicket && scrollAnchorNextTicketIdRef.current) {
                const nextId = scrollAnchorNextTicketIdRef.current;
                const boundary = findNextTicketBoundaryMessage(messages, nextId);
                const boundaryEl = boundary && document.getElementById(boundary.id);
                if (boundaryEl) {
                    boundaryEl.scrollIntoView({
                        block: 'end',
                        behavior: 'auto',
                    });
                } else if (scrollAnchorFallbackIdRef.current) {
                    const fallbackEl = document.getElementById(scrollAnchorFallbackIdRef.current);
                    if (fallbackEl) {
                        fallbackEl.scrollIntoView({
                            block: 'end',
                            behavior: 'auto',
                        });
                    }
                }
                scrollAnchorNextTicketIdRef.current = null;
                scrollAnchorFallbackIdRef.current = null;
            }
        }, [loadingNextTicket, messages]);

        const realMessages = useMemo(() => {
            if (targetVirtualizedMessage) {
                const index = messages.findIndex((message) => message.id === targetVirtualizedMessage.id);
                if (index > -1) {
                    return messages.slice(0, index + 1);
                }
                return messages;
            } else {
                return messages;
            }
        }, [messages, targetVirtualizedMessage]);

        return (
            <Container>
                <ContainerScreenChat background={"transparent"}>
                    <PickFileGeneric
                        disabled={!canEditFile}
                        maxFiles={20}
                        maxSizeInMegaBytes={16}
                        onPick={(files: File[]) => {
                            handlePickFile(files);
                        }}
                        noClick={true}
                        isMultiple={true}
                        noDrag={false}
                        renderComponent={
                            <ContainerChatMessageInput>

                                <ContentChatMessageAndInput
                                    ref={containerChatRef}
                                    bordercolor={colors.border}
                                >


                                    {bottomMessageLoading &&
                                        <ContentLoading>
                                            <LoadingMoreMessageBottom
                                                background={colors.delicateBackground}
                                                isDarkTheme={isDarkTheme}
                                                border={colors.border}
                                            >
                                                <ClipLoading color={colors.accent} />
                                            </LoadingMoreMessageBottom>
                                        </ContentLoading>
                                    }
                                    {showLoadNextTicketButton && !bottomMessageLoading && onLoadNextTicket &&
                                        <LoadAdjacentTicketButton
                                            onClick={handleLoadNextTicket}
                                            loading={!!loadingNextTicket}
                                            direction={'down'}
                                        />
                                    }
                                    <MessagesBox
                                        messages={realMessages}
                                        userClient={userClient}
                                        attendant={attendant}
                                        idMessageFocused={idMessageFocused}
                                        handleClickOnMessageReply={handleClickOnMessageReply}
                                        cancelUpload={(uploadTask) => cancelUpload(uploadTask)}
                                        onRetrySendMessageError={(message) => onRetrySendMessageError(message)}
                                        canChange={canChange}
                                        userLogged={userLogged}
                                        handleRemoveReaction={handleRemoveReaction}
                                        handleAddReaction={(reaction, message) => handleAddReaction(reaction, message)}
                                        lastUsedReactions={lastUsedReactions}
                                        handleEditMessage={handleEditMessage}
                                        handleDeleteMessage={handleDeleteMessage}
                                        setMessageToReply={setMessageToReply}
                                        handleResetMessageFocused={handleResetMessageFocused}
                                        compactModeMessages={compactModeMessages}
                                        idParent={idParent}
                                        allReactions={allReactions}
                                        isMeAttendingTicket={isMeAttendingTicket}
                                        ticketIsOpen={ticketIsOpen}
                                        showOriginalFile={showOriginalFile}
                                        setLoaded={setLoaded}
                                        startingServiceInternalChatHeaderText={startingServiceInternalChatHeaderText}
                                        closingServiceInternalChatHeaderText={closingServiceInternalChatHeaderText}
                                    />
                                    {showLoadPreviousTicketButton && !topMessageLoading && onLoadPreviousTicket &&
                                        <LoadAdjacentTicketButton
                                            onClick={onLoadPreviousTicket}
                                            loading={!!loadingPreviousTicket}
                                            direction={'up'}
                                        />
                                    }
                                    {topMessageLoading &&
                                        <ContentLoading>
                                            <LoadingMoreMessageTop
                                                background={colors.onBackground}
                                                isDarkTheme={isDarkTheme}
                                                border={colors.border}
                                            >
                                                <ClipLoading color={colors.accent} />
                                            </LoadingMoreMessageTop>
                                        </ContentLoading>
                                    }
                                </ContentChatMessageAndInput>
                                {showButtonMoveScrollToBottom &&
                                    <ButtonNavigateToBottom
                                        onClick={() => handleOnClickMoveCursorToFinal()}
                                    />
                                }
                                {canSendMessage &&
                                    <InputMessage
                                        onSendMessage={sendMessage}
                                        canSendMessage={canSendMessage}
                                        forcePrivateComment={forcePrivateComment}
                                        handlePickFile={handlePickFile}
                                        userLogged={userLogged}
                                        sendAudio={sendAudio}
                                        ref={childRef}
                                        moveScrollToBottom={handleOnClickMoveCursorToFinal}
                                        idParent={idParent}
                                        createNewMessage={createNewMessage}
                                    />}

                            </ContainerChatMessageInput>
                        }
                    />
                </ContainerScreenChat>
            </Container>
        )
    })
    ;
export default ChatMessage;
